import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ACCESS_LEVELS,
  ACCESS_LEVEL_LABELS,
  REVIEW_FREQUENCIES,
  REVIEW_FREQUENCY_LABELS,
  type AssignmentAccessLevel,
  type CollectionDef,
  type OrgMember,
  type OrgRecord,
  type RecordAssignment,
  type ReviewFrequency,
  type UpdateAssignmentInput,
} from '@grc/types';
import { apiFetch } from '../../lib/api.js';
import { useAuth, useOrg } from '../../lib/auth.js';
import {
  Button,
  Dialog,
  EmptyState,
  Field,
  Input,
  Select,
  Table,
  formatDate,
  useToast,
} from '../../components/ui.js';
import { NextReview, accessLevelLabel, apiErrorMessage, localDateIso } from './AccessReviewShared.js';
import './records.css';

/** The grant details captured when assigning or editing an assignment (spec §5.1). */
interface GrantForm {
  accessLevel: AssignmentAccessLevel;
  accessRole: string;
  /** '' = not recorded. */
  approvedBy: string;
  grantedOn: string;
  reviewFrequency: ReviewFrequency;
  notes: string;
}

function blankForm(currentUserId: string): GrantForm {
  return {
    accessLevel: '',
    accessRole: '',
    approvedBy: currentUserId,
    grantedOn: localDateIso(),
    reviewFrequency: 'annual',
    notes: '',
  };
}

function formFor(a: RecordAssignment): GrantForm {
  return {
    accessLevel: a.accessLevel,
    accessRole: a.accessRole,
    approvedBy: a.approvedBy ?? '',
    grantedOn: a.grantedOn ?? '',
    reviewFrequency: a.reviewFrequency,
    notes: a.notes,
  };
}

/**
 * Only the fields that differ from the stored grant: the server re-anchors the
 * next review date whenever grantedOn/reviewFrequency are present, and audits
 * exactly what was sent.
 */
function diffForm(form: GrantForm, a: RecordAssignment): UpdateAssignmentInput {
  const patch: UpdateAssignmentInput = {};
  if (form.accessLevel !== a.accessLevel) patch.accessLevel = form.accessLevel;
  if (form.accessRole.trim() !== a.accessRole) patch.accessRole = form.accessRole.trim();
  if ((form.approvedBy || null) !== a.approvedBy) patch.approvedBy = form.approvedBy || null;
  if (form.grantedOn && form.grantedOn !== a.grantedOn) patch.grantedOn = form.grantedOn;
  if (form.reviewFrequency !== a.reviewFrequency) patch.reviewFrequency = form.reviewFrequency;
  if (form.notes.trim() !== a.notes) patch.notes = form.notes.trim();
  return patch;
}

/** Manage who a record is assigned to (software access, issued hardware). */
export function AssignmentsDialog({
  def,
  record,
  open,
  onClose,
}: {
  def: CollectionDef;
  record: OrgRecord;
  open: boolean;
  onClose: () => void;
}) {
  const { org } = useOrg();
  const { me } = useAuth();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const canWrite = org.role === 'admin' || org.role === 'contributor';
  const noun = def.assignmentNoun ?? 'assignment';
  const currentUserId = me?.user.id ?? '';

  const assignments = useQuery({
    queryKey: ['record-assignments', org.orgId, record.id],
    queryFn: () =>
      apiFetch<{ items: RecordAssignment[] }>(`/orgs/${org.orgId}/record-assignments`, {
        query: { recordId: record.id },
      }),
    enabled: open,
  });
  const members = useQuery({
    queryKey: ['members', org.orgId],
    queryFn: () => apiFetch<OrgMember[]>(`/orgs/${org.orgId}/members`),
    enabled: open && canWrite,
  });

  const [userId, setUserId] = useState('');
  const [editing, setEditing] = useState<RecordAssignment | null>(null);
  const [form, setForm] = useState<GrantForm>(() => blankForm(currentUserId));
  const setField = <K extends keyof GrantForm>(key: K, value: GrantForm[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const resetForm = () => {
    setEditing(null);
    setUserId('');
    setForm(blankForm(currentUserId));
  };
  const close = () => {
    resetForm();
    onClose();
  };
  const startEdit = (a: RecordAssignment) => {
    setEditing(a);
    setForm(formFor(a));
  };

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['record-assignments', org.orgId] });
  };
  const onError = (err: unknown) => toast(apiErrorMessage(err, 'Something went wrong'), 'error');

  const assign = useMutation({
    mutationFn: () =>
      apiFetch<RecordAssignment>(`/orgs/${org.orgId}/records/${record.id}/assignments`, {
        method: 'POST',
        body: {
          userId,
          notes: form.notes.trim(),
          accessLevel: form.accessLevel,
          accessRole: form.accessRole.trim(),
          approvedBy: form.approvedBy || null,
          grantedOn: form.grantedOn || undefined,
          reviewFrequency: form.reviewFrequency,
        },
      }),
    onSuccess: () => {
      resetForm();
      toast('Assigned.', 'success');
      invalidate();
    },
    onError,
  });

  const update = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: UpdateAssignmentInput }) =>
      apiFetch<RecordAssignment>(
        `/orgs/${org.orgId}/records/${record.id}/assignments/${id}`,
        { method: 'PATCH', body: patch },
      ),
    onSuccess: () => {
      resetForm();
      toast('Access details updated.', 'success');
      invalidate();
    },
    onError,
  });

  const revoke = useMutation({
    mutationFn: (assignmentId: string) =>
      apiFetch<RecordAssignment>(
        `/orgs/${org.orgId}/records/${record.id}/assignments/${assignmentId}/revoke`,
        { method: 'POST' },
      ),
    onSuccess: (_data, assignmentId) => {
      if (editing?.id === assignmentId) resetForm();
      toast('Assignment revoked.', 'success');
      invalidate();
    },
    onError,
  });

  const saveEdit = () => {
    if (!editing) return;
    const patch = diffForm(form, editing);
    if (Object.keys(patch).length === 0) {
      resetForm();
      return;
    }
    update.mutate({ id: editing.id, patch });
  };

  const items = assignments.data?.items ?? [];
  const assignedIds = new Set(items.map((a) => a.userId));
  const memberList = members.data ?? [];
  // A former member who approved a grant must still be selectable so the
  // stored value survives an edit of the other fields.
  const approverMissing =
    form.approvedBy !== '' && !memberList.some((m) => m.user.id === form.approvedBy);

  const grantFields = (
    <>
      <div className="grant-form-grid">
        <Field label="Access level">
          {(props) => (
            <Select
              {...props}
              value={form.accessLevel}
              onChange={(e) => setField('accessLevel', e.target.value as AssignmentAccessLevel)}
            >
              <option value="">Not recorded</option>
              {ACCESS_LEVELS.map((level) => (
                <option key={level} value={level}>
                  {ACCESS_LEVEL_LABELS[level]}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field label="Role name" hint="e.g. Global admin, Maintainer">
          {(props) => (
            <Input
              {...props}
              value={form.accessRole}
              maxLength={100}
              onChange={(e) => setField('accessRole', e.target.value)}
            />
          )}
        </Field>
        <Field label="Approved by" hint="Who allowed this access">
          {(props) => (
            <Select
              {...props}
              value={form.approvedBy}
              onChange={(e) => setField('approvedBy', e.target.value)}
            >
              <option value="">Not recorded</option>
              {approverMissing && (
                <option value={form.approvedBy}>
                  {editing?.approvedByName ?? 'Former member'}
                </option>
              )}
              {memberList.map((m) => (
                <option key={m.user.id} value={m.user.id}>
                  {m.user.name}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field label="Date granted">
          {(props) => (
            <Input
              {...props}
              type="date"
              value={form.grantedOn}
              onChange={(e) => setField('grantedOn', e.target.value)}
            />
          )}
        </Field>
        <Field label="Review frequency" hint="How often this grant must be re-certified">
          {(props) => (
            <Select
              {...props}
              value={form.reviewFrequency}
              onChange={(e) => setField('reviewFrequency', e.target.value as ReviewFrequency)}
            >
              {REVIEW_FREQUENCIES.map((f) => (
                <option key={f} value={f}>
                  {REVIEW_FREQUENCY_LABELS[f]}
                </option>
              ))}
            </Select>
          )}
        </Field>
      </div>
      <Field label="Notes" hint="Optional — e.g. condition, licence key reference">
        {(props) => (
          <Input
            {...props}
            value={form.notes}
            maxLength={500}
            onChange={(e) => setField('notes', e.target.value)}
          />
        )}
      </Field>
    </>
  );

  return (
    <Dialog open={open} onClose={close} title={`${record.name} — ${noun} assignments`}>
      <div className="record-dialog assignments-dialog">
        {items.length === 0 ? (
          <EmptyState
            title={`No ${noun} assignments`}
            message={`No one currently holds this ${def.singular}.`}
          />
        ) : (
          <Table
            head={
              <>
                <th>Member</th>
                <th>Level</th>
                <th>Granted</th>
                <th>Approved by</th>
                <th>Next review</th>
                <th>Last reviewed</th>
                {canWrite && <th />}
              </>
            }
          >
            {items.map((a) => (
              <tr key={a.id} aria-current={editing?.id === a.id ? 'true' : undefined}>
                <td>
                  <div style={{ fontWeight: 600 }}>{a.userName}</div>
                  <span className="cell-muted">{a.userEmail}</span>
                  {a.notes && <span className="cell-muted">{a.notes}</span>}
                </td>
                <td>
                  {accessLevelLabel(a.accessLevel)}
                  {a.accessRole && <span className="cell-muted">{a.accessRole}</span>}
                </td>
                <td style={{ whiteSpace: 'nowrap' }}>{formatDate(a.grantedOn)}</td>
                <td>{a.approvedByName ?? '—'}</td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  <NextReview assignment={a} />
                </td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  {a.lastReviewedAt ? (
                    <>
                      {formatDate(a.lastReviewedAt)}
                      {a.lastReviewedByName && (
                        <span className="cell-muted">{a.lastReviewedByName}</span>
                      )}
                    </>
                  ) : (
                    'Never'
                  )}
                </td>
                {canWrite && (
                  <td>
                    <div className="record-row-actions">
                      <Button
                        variant="secondary"
                        size="sm"
                        aria-label={`Edit ${a.userName}'s access details`}
                        onClick={() => startEdit(a)}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        aria-label={`Revoke ${a.userName}'s ${noun}`}
                        loading={revoke.isPending && revoke.variables === a.id}
                        onClick={() => revoke.mutate(a.id)}
                      >
                        Revoke
                      </Button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </Table>
        )}

        {canWrite && editing && (
          <section className="grant-form" aria-label={`Edit ${editing.userName}'s access details`}>
            <h3 className="card-title">Edit {editing.userName}&rsquo;s access details</h3>
            {grantFields}
            <div className="dialog-actions" style={{ marginTop: 0 }}>
              <Button variant="secondary" onClick={resetForm}>
                Cancel
              </Button>
              <Button loading={update.isPending} onClick={saveEdit}>
                Save changes
              </Button>
            </div>
          </section>
        )}

        {canWrite && !editing && (
          <section className="grant-form" aria-label={`Assign this ${def.singular}`}>
            <Field label={`Assign this ${def.singular} to`}>
              {(props) => (
                <Select {...props} value={userId} onChange={(e) => setUserId(e.target.value)}>
                  <option value="">Select a member…</option>
                  {memberList
                    .filter((m) => !assignedIds.has(m.user.id))
                    .map((m) => (
                      <option key={m.user.id} value={m.user.id}>
                        {m.user.name}
                      </option>
                    ))}
                </Select>
              )}
            </Field>
            {grantFields}
          </section>
        )}

        <div className="dialog-actions">
          <Button variant="secondary" onClick={close}>
            Close
          </Button>
          {canWrite && !editing && (
            <Button disabled={!userId} loading={assign.isPending} onClick={() => assign.mutate()}>
              Assign
            </Button>
          )}
        </div>
      </div>
    </Dialog>
  );
}
